import { useCallback, useMemo } from 'react';
import { get } from 'lodash-es';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';

import style from './bulk-actions.module.less';
import { messages } from './messages';
import ShippingServiceSelector from '../../../components/shipping-service-selector';
import { BulkActions } from '../../../enums/bulk-actions';
import { BulkFulfillmentPlanTypeEnum } from '../../../enums/bulk-fulfillment-plan-type';
import { BulkFulfillmentTypeMap } from '../../../enums/bulk-fulfillment-type';
import BulkFulfillmentServiceDropdownContainer from '../../../fulfillment-planner/fulfillment-service/bulk-container';
import ShippingServiceLabel from '../../../fulfillment-planner/shipping-service/label';
import { ACCESS_LEVELS, useRequiresRateShopperAccessLevel } from '../../../hooks/use-has-rate-shopper-access-level';
import { useIsVendorOr3PL } from '../../../hooks/use-is-vendor-or-3pl';
import { useSelectedOrdersView } from '../../../pages/orders-page/orders-left-nav/orders-left-nav-context';
import { CarrierSharingService } from '../../../store/carrier-sharing/carrier-sharing-service';
import Global from '../../../store/global-selectors';
import useProviderOrCarrierGroups from '../../shipping-service-selector/group-hooks/use-provider-or-carrier-groups';
import useServiceCollectionGroup from '../../shipping-service-selector/group-hooks/use-service-collection-group';
import useSelectedItemName from '../../shipping-service-selector/use-selected-item-name';

const getInternationalFilter = (jobData) => (service) => {
  const bulkFulfillmentPlanType = get(jobData, 'bulkFulfillmentPlanType');
  if (bulkFulfillmentPlanType === BulkFulfillmentPlanTypeEnum.Domestic || bulkFulfillmentPlanType === BulkFulfillmentPlanTypeEnum.Mixed) {
    return true;
  }
  return get(service, 'features.supportsInternationalShipping', false);
};

export const serviceDropdownHasErrors = (bulkFulfillmentType, sellerFulfillmentProviderIdFromJobData) =>
  bulkFulfillmentType === BulkFulfillmentTypeMap.Mixed ||
  (bulkFulfillmentType === BulkFulfillmentTypeMap.External && !sellerFulfillmentProviderIdFromJobData);

const modelShouldDictateServices = (model, bulkFulfillmentType) =>
  !!model?.externalFulfillmentConfiguration?.sellerFulfillmentProviderId ||
  !!model?.shippingConfiguration?.shipFromId ||
  (!!model?.shippingConfiguration?.sellerLabelProviderId && bulkFulfillmentType !== BulkFulfillmentTypeMap.Mixed);

const ServiceDropdown = ({ onChange, model, jobData, isEmpty }) => {
  const isVendorOr3PL = useIsVendorOr3PL();
  const allServices = useSelector(Global.selectSellerExtendedServices);
  const { shippingConfiguration, externalFulfillmentConfiguration } = model ?? {};
  const {
    shipFromId,
    serviceId: selectedServiceId,
    sellerLabelProviderId: selectedSellerLabelProviderId,
    serviceCollectionGuid: selectedServiceCollectionGuid,
  } = shippingConfiguration ?? {};
  const { sellerFulfillmentProviderId: fulfillmentProviderFromModel } = externalFulfillmentConfiguration ?? {};
  const { sellerFulfillmentProviderId: fulfillmentProviderFromJobData, bulkFulfillmentType } = jobData ?? {};

  const allOwnShipments = bulkFulfillmentType === BulkFulfillmentTypeMap.Label;
  const allExternalFulfillments = bulkFulfillmentType === BulkFulfillmentTypeMap.External;
  const hasErrors = !modelShouldDictateServices(model, bulkFulfillmentType) && !allOwnShipments && !fulfillmentProviderFromJobData;

  const allExternalFulfillmentsHaveSameProvider =
    !!fulfillmentProviderFromModel || (allExternalFulfillments && !!fulfillmentProviderFromJobData);

  const shouldShowFulfillmentServices = !hasErrors && allExternalFulfillmentsHaveSameProvider && !shipFromId;
  const shouldShowShippingServices = !hasErrors && ((allOwnShipments && !fulfillmentProviderFromModel) || !!shipFromId);
  const shouldShowLabel = shouldShowShippingServices || shouldShowFulfillmentServices;

  const internationalFilter = useMemo(() => getInternationalFilter(jobData), [jobData]);
  const filteredServices = useMemo(() => allServices.filter(internationalFilter), [internationalFilter, allServices]);

  const onServiceChange = useCallback(
    ({ serviceId, sellerLabelProviderId, serviceCollectionGuid }) => {
      onChange({
        ...model,
        shippingConfiguration: {
          ...shippingConfiguration,
          sellerLabelProviderId,
          serviceId,
          serviceCollectionGuid,
        },
      });
    },
    [model, shippingConfiguration, onChange],
  );

  const selectedItem = {
    serviceId: selectedServiceId,
    sellerLabelProviderId: selectedSellerLabelProviderId,
    serviceCollectionGuid: selectedServiceCollectionGuid,
  };
  let selectedItemName = useSelectedItemName(selectedItem);
  let serviceGroups = useProviderOrCarrierGroups(filteredServices, selectedItem);

  const showRateShopper = useRequiresRateShopperAccessLevel(ACCESS_LEVELS.FREEMIUM);
  const serviceCollectionGroup = useServiceCollectionGroup(selectedItem);
  if (showRateShopper) {
    serviceGroups.unshift(serviceCollectionGroup);
  }
  const { storeGuid } = useSelectedOrdersView();
  const sharedByPartnerServices = useSelector(Global.selectSellerPartnerSharedServices);

  if (isVendorOr3PL) {
    // service group extended by carrier sharing
    serviceGroups = CarrierSharingService.combineSharedServices(serviceGroups, storeGuid, sharedByPartnerServices);
    // when user choose shared service by client the selected item name must be selected from shared services instead of seller services
    selectedItemName = CarrierSharingService.selectedServiceNameByStore(selectedServiceId, selectedItemName, sharedByPartnerServices);
  }

  const renderShippingServiceSelector = useCallback(() => {
    if (shouldShowShippingServices) {
      return (
        <ShippingServiceSelector
          className={style['bulk-action-dropdown']}
          defaultEmptyText={messages.shippingServiceDropdownPlaceholder}
          dropdownTogglerClassName={style['bulk-action-dropdown-toggler']}
          hasError={isEmpty}
          onSelected={onServiceChange}
          selectedItemName={selectedItemName}
          serviceGroups={serviceGroups}
        />
      );
    }
  }, [isEmpty, onServiceChange, serviceGroups, shouldShowShippingServices, selectedItemName]);

  return (
    <>
      {hasErrors ? (
        <BannerMessageV2 content={<FormattedMessage {...messages.serviceUpdateNotAllowedError} />} status={BannerStatus.Error} />
      ) : null}
      <div className={style['dropdown-with-label']}>
        <div className={style.label}>{shouldShowLabel ? <ShippingServiceLabel /> : null}</div>
        {shouldShowFulfillmentServices ? (
          <BulkFulfillmentServiceDropdownContainer
            className={style['bulk-action-dropdown']}
            model={model}
            onChange={onChange}
            sellerFulfillmentProviderId={
              externalFulfillmentConfiguration?.sellerFulfillmentProviderId ?? fulfillmentProviderFromJobData?.toString()
            }
          />
        ) : null}
        {renderShippingServiceSelector()}
      </div>
    </>
  );
};

ServiceDropdown.propTypes = {
  onChange: PropTypes.func.isRequired,
  model: PropTypes.object.isRequired,
  jobData: PropTypes.object.isRequired,
  isEmpty: PropTypes.bool.isRequired,
};

export const ShippingService = {
  id: BulkActions.service.name,
  render: (onChange, model, jobData, isEmpty) => <ServiceDropdown isEmpty={isEmpty} jobData={jobData} model={model} onChange={onChange} />,
};
